import { useEffect, useState } from 'react';
import { FaBell, FaCheck, FaTimes } from 'react-icons/fa';
import { supabase } from '../supabaseClient';
import { EVENT } from '../data/eventInfo';

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

/**
 * "Remind Me" sits next to the Register Soon button while registration is
 * closed. Tapping it opens a short explainer and takes one email address,
 * which gets the announcement the day registration opens.
 */
export default function GetReminded({ className = '' }) {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle'); // idle | saving | done | error
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [open]);

  const close = () => {
    setOpen(false);
    if (status !== 'done') { setStatus('idle'); setMessage(''); }
  };

  const submit = async (e) => {
    e.preventDefault();
    const value = email.trim().toLowerCase();

    if (!EMAIL_RE.test(value)) {
      setStatus('error');
      setMessage('That email does not look right.');
      return;
    }

    setStatus('saving');
    const { error } = await supabase.from('wcmc_reminders').insert({ email: value });

    // 23505 = already on the list, which is a success from their side
    if (error && error.code !== '23505') {
      setStatus('error');
      setMessage('Something went wrong. Please try again.');
      return;
    }

    setStatus('done');
    setMessage(value);
  };

  return (
    <>
      <button type="button" className={`remind-trigger ${className}`} onClick={() => setOpen(true)}>
        <FaBell className="remind-trigger-icon" /> Remind Me
      </button>

      {open && (
        <div className="remind-backdrop" onClick={close} role="presentation">
          <div
            className="remind-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="remind-title"
          >
            <button type="button" className="remind-close" onClick={close} aria-label="Close">
              <FaTimes />
            </button>

            {status === 'done' ? (
              <div className="remind-done">
                <FaCheck className="remind-done-icon" />
                <h3 id="remind-title">You're on the list</h3>
                <p>
                  We'll email <strong>{message}</strong> the day registration opens for
                  {' '}{EVENT.dateDisplay}. One email, nothing else.
                </p>
                <button type="button" className="cta-button" onClick={close}>Done</button>
              </div>
            ) : (
              <>
                <FaBell className="remind-modal-icon" />
                <h3 id="remind-title">Get reminded when registration opens</h3>
                <p className="remind-explainer">
                  Registration for {EVENT.dateDisplay} in {EVENT.shortLocation} isn't open yet.
                  Leave your email and we'll send you one message the day it is, so you can grab
                  a spot before the weekend fills up.
                </p>
                <p className="remind-fineprint">
                  One email about registration. No newsletter, and we never share your address.
                </p>
                <form onSubmit={submit}>
                  <input
                    type="email"
                    className="remind-input"
                    placeholder="you@email.com"
                    value={email}
                    onChange={(e) => { setEmail(e.target.value); if (status === 'error') setStatus('idle'); }}
                    autoComplete="email"
                    autoFocus
                    required
                  />
                  {status === 'error' && <p className="remind-error">{message}</p>}
                  <button type="submit" className="cta-button remind-submit" disabled={status === 'saving'}>
                    {status === 'saving' ? 'Adding…' : 'Remind Me'}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
